/**
 * Holiday pre-order package builder — multi-item cart add + checkout redirect.
 */
class ThanksgivingPreorder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-thanksgiving-form]');
    this.subtotalEl = this.querySelector('[data-subtotal]');
    this.errorEl = this.querySelector('[data-error]');
    this.submitBtn = this.querySelector('[data-submit]');

    if (!this.form) return;

    this.form.addEventListener('input', (event) => {
      if (event.target.matches('[data-qty], [data-variant-select], [data-delivery-option]')) {
        this.updateSubtotal();
      }
    });

    this.form.addEventListener('change', (event) => {
      if (event.target.matches('[data-qty], [data-variant-select], [data-delivery-option]')) {
        this.updateSubtotal();
      }
    });

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));

    this.updateSubtotal();
  }

  /** @param {string|number|undefined} value */
  parsePrice(value) {
    const cents = Number(value);
    return Number.isFinite(cents) && cents > 0 ? cents : 0;
  }

  /** @param {string|undefined|null} text */
  parsePriceLabel(text) {
    if (!text) return 0;
    const normalized = text.trim().toLowerCase();
    if (!normalized || normalized === 'free') return 0;

    const amount = parseFloat(normalized.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(amount)) return 0;

    return Math.round(amount * 100);
  }

  /** @param {Element} row */
  getRowUnitCents(row) {
    const variantSelect = row.querySelector('[data-variant-select]');
    let cents = this.parsePrice(row.dataset.priceCents);

    if (variantSelect?.selectedOptions[0]) {
      cents = this.parsePrice(variantSelect.selectedOptions[0].dataset.priceCents) || cents;
    }

    if (cents > 0) return cents;

    const priceEl = row.querySelector('.thanksgiving-preorder__line-price');
    return this.parsePriceLabel(priceEl?.textContent);
  }

  /** @returns {Array<{input: HTMLInputElement|null, variantSelect: HTMLSelectElement|null, row: Element}>} */
  getLineInputs() {
    return Array.from(this.querySelectorAll('[data-line-item]')).map((row) => ({
      input: row.querySelector('[data-qty]'),
      variantSelect: row.querySelector('[data-variant-select]'),
      row,
    }));
  }

  /** @param {HTMLInputElement} deliveryInput */
  getDeliveryCents(deliveryInput) {
    let cents = this.parsePrice(deliveryInput.dataset.priceCents);
    if (cents > 0) return cents;

    const priceEl = deliveryInput
      .closest('.thanksgiving-preorder__delivery-label')
      ?.querySelector('.thanksgiving-preorder__line-price');

    return this.parsePriceLabel(priceEl?.textContent);
  }

  updateSubtotal() {
    let subtotalCents = 0;

    for (const { input, row } of this.getLineInputs()) {
      if (!input) continue;

      const qty = parseInt(input.value, 10) || 0;
      if (qty <= 0) continue;

      subtotalCents += this.getRowUnitCents(row) * qty;
    }

    const delivery = this.querySelector('[data-delivery-option]:checked');
    if (delivery instanceof HTMLInputElement) {
      subtotalCents += this.getDeliveryCents(delivery);
    }

    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.formatMoney(subtotalCents);
    }
  }

  /** @param {number} cents */
  formatMoney(cents) {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(cents / 100);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.hidden = false;
    this.errorEl.textContent = message;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  /** @param {SubmitEvent} event */
  async handleSubmit(event) {
    event.preventDefault();
    this.clearError();

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    /** @type {Array<{id: number, quantity: number, properties?: Record<string, string>}>} */
    const items = [];
    const contactProps = this.getContactProperties();

    for (const { input, variantSelect, row } of this.getLineInputs()) {
      if (!input) continue;
      const qty = parseInt(input.value, 10) || 0;
      if (qty <= 0) continue;

      let variantId = row.dataset.variantId;
      if (variantSelect?.value) {
        variantId = variantSelect.value;
      }

      if (!variantId) {
        this.showError('Some menu items are not connected to products yet. Please contact us to complete your order.');
        return;
      }

      items.push({
        id: Number(variantId),
        quantity: qty,
        properties: contactProps,
      });
    }

    const delivery = this.querySelector('[data-delivery-option]:checked');
    if (delivery instanceof HTMLInputElement && delivery.value) {
      items.push({
        id: Number(delivery.value),
        quantity: 1,
        properties: contactProps,
      });
    }

    if (items.length === 0) {
      this.showError('Please select at least one item.');
      return;
    }

    // Client-side guard: never send a $0 cart to checkout
    let estimatedCents = 0;
    for (const { input, row } of this.getLineInputs()) {
      if (!input) continue;
      const qty = parseInt(input.value, 10) || 0;
      if (qty <= 0) continue;
      estimatedCents += this.getRowUnitCents(row) * qty;
    }
    if (delivery instanceof HTMLInputElement) {
      estimatedCents += this.getDeliveryCents(delivery);
    }
    if (estimatedCents <= 0) {
      this.showError(
        'This order totals $0, so checkout is closed. For donations or special requests, email info@funkyfork.ca.'
      );
      return;
    }

    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.setAttribute('aria-busy', 'true');
    }

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!addResponse.ok) {
        const err = await addResponse.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add items to cart.');
      }

      const note = this.buildCartNote(contactProps);
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          note,
          attributes: {
            'Contact name': contactProps['Contact name'] || '',
            'Contact email': contactProps['Contact email'] || '',
            'Contact phone': contactProps['Contact phone'] || '',
            'Pickup date': contactProps['Pickup date'] || '',
            'Guest count': contactProps['Guest count'] || '',
          },
        }),
      });

      const cartResponse = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json().catch(() => ({}));
      if (Number(cart.total_price) <= 0) {
        throw new Error(
          'This cart totals $0, so checkout is closed. For donations or special requests, email info@funkyfork.ca.'
        );
      }

      window.location.href = '/checkout';
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        this.submitBtn.removeAttribute('aria-busy');
      }
    }
  }

  /** @returns {Record<string, string>} */
  getContactProperties() {
    const formData = new FormData(this.form);
    return {
      'Contact name': String(formData.get('contact_name') || ''),
      'Contact email': String(formData.get('contact_email') || ''),
      'Contact phone': String(formData.get('contact_phone') || ''),
      'Pickup date': String(formData.get('pickup_date') || ''),
      'Guest count': String(formData.get('guest_count') || ''),
      Notes: String(formData.get('order_notes') || ''),
    };
  }

  /** @param {Record<string, string>} props */
  buildCartNote(props) {
    return [
      'Holiday Pre-Order',
      `Name: ${props['Contact name']}`,
      `Email: ${props['Contact email']}`,
      `Phone: ${props['Contact phone']}`,
      `Pickup date: ${props['Pickup date']}`,
      `Guests: ${props['Guest count']}`,
      props.Notes ? `Notes: ${props.Notes}` : '',
    ]
      .filter(Boolean)
      .join('\n');
  }
}

if (!customElements.get('thanksgiving-preorder')) {
  customElements.define('thanksgiving-preorder', ThanksgivingPreorder);
}
